import { useState, type FormEvent } from "react";
import { formatArs } from "../../shared/money";
import {
  estimateShipping,
  freeShippingEnabled,
  freeShippingMin,
  freeShippingZones,
  pickupArea,
  pickupEnabled,
  postalArea,
  shippingRates,
  type ShippingEstimate,
} from "../../shared/shipping";
import { useI18n } from "../i18n/I18nProvider";
import { useCatalog } from "../lib/CatalogProvider";

/** Remembered between products and handed to the checkout's address. */
export const POSTAL_CODE_KEY = "pf-postal-code";

export function savedPostalCode(): string {
  try {
    return localStorage.getItem(POSTAL_CODE_KEY) ?? "";
  } catch {
    return "";
  }
}

/**
 * "Calculá el envío" on the product page: a postal code gives the cost right there (CABA exact,
 * Greater Buenos Aires as the range of its rings, the rest of the country at the flat rate or
 * "a coordinar"), so the price doesn't show up for the first time at checkout.
 */
export function ShippingEstimator() {
  const { t, pick, locale } = useI18n();
  const { settings } = useCatalog();
  const value = (k: string) => (settings[k] ? pick(settings[k]) : null);
  const [code, setCode] = useState(savedPostalCode);
  const [shown, setShown] = useState<string>(() => (postalArea(savedPostalCode()) ? savedPostalCode() : ""));
  const [invalid, setInvalid] = useState(false);

  const shipping = {
    rates: shippingRates(value("shipping_rates_ars")),
    freeEnabled: freeShippingEnabled(value("free_shipping_enabled")),
    freeMinArs: freeShippingMin(value("free_shipping_min_ars")),
    freeZones: freeShippingZones(value("free_shipping_zones")),
  };
  const area = shown ? postalArea(shown) : null;
  const estimate = area ? estimateShipping(area, shipping) : null;
  const ars = (n: number) => formatArs(n, locale);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!postalArea(code)) {
      setInvalid(true);
      return;
    }
    setInvalid(false);
    setShown(code.trim().toUpperCase());
    try {
      localStorage.setItem(POSTAL_CODE_KEY, code.trim().toUpperCase());
    } catch {
      // private mode: it works for this page only
    }
  };

  const line = (est: ShippingEstimate): string => {
    if (est.kind === "arrange") return t("ship.arrange");
    if (est.kind === "range") return t("ship.gbaRange", { min: ars(est.min), max: ars(est.max) });
    return t(area === "caba" ? "ship.caba" : area === "gba" ? "ship.gba" : "ship.country", { price: ars(est.ars) });
  };

  return (
    <section className="ship-estimator" aria-labelledby="ship-title">
      <form className="ship-form" onSubmit={submit}>
        <label id="ship-title" htmlFor="ship-cp">
          {t("ship.title")}
        </label>
        <div className="ship-row">
          <input
            id="ship-cp"
            inputMode="text"
            autoComplete="postal-code"
            placeholder={t("ship.placeholder")}
            value={code}
            maxLength={8}
            onChange={(e) => setCode(e.target.value)}
            aria-invalid={invalid}
            aria-describedby={invalid ? "ship-err" : undefined}
          />
          <button type="submit" className="btn btn-outline btn-sm">
            {t("ship.calculate")}
          </button>
        </div>
      </form>
      {invalid && (
        <p id="ship-err" className="field-error">
          {t("ship.invalid")}
        </p>
      )}
      {estimate && (
        <ul className="ship-result" aria-live="polite">
          <li>
            🚚 {line(estimate)}
            {estimate.kind !== "arrange" && estimate.freeFromArs !== null && <span className="ship-free"> {t("ship.freeFrom", { min: ars(estimate.freeFromArs) })}</span>}
          </li>
          {pickupEnabled(value("pickup_enabled")) && <li>📍 {t("ship.pickup", { area: pickupArea(value("pickup_area")) })}</li>}
        </ul>
      )}
    </section>
  );
}
